import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ActionButton from '../../components/ActionButton/ActionButton';
import AnnotationCanvas from '../../components/AnnotationCanvas/AnnotationCanvas';
import EditToolList from '../../components/EditToolList/EditToolList';
import Header from '../../components/Header/Header';
import InputField from '../../components/InputField/InputField';
import Loading from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import SegmentationCanvas from '../../components/SegmentationCanvas/SegmentationCanvas';
import SnackBar from '../../components/SnackBar/SnackBar';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import roundToPrecision from '../../utils/roundToPrecision';
import EDIT_TOOL_TYPES from '../../data/edit-tool-types';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { useIsMounted } from '../../utils/useIsMounted';
import { useKeyDown } from '../../utils/useKeyDown';
import { useKeyUp } from '../../utils/useKeyUp';
import { breakpoints } from '../../ui/variables';

import { EditToolStyle } from './EditToolStyle';

const MAX_DENSITY_MIN_LIMITS = 100;

const EditTool = () => {
  const breakpoint = useBreakpoint();
  const history = useHistory();
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);
  const { id } = useParams();

  const orignalContainerRef = useRef();

  const [actionsList, setActionsList] = useState([]);
  const [annotationId, setAnnotationId] = useState();
  const [annotationList, setAnnotationList] = useState([]);
  const [annotationTypes, setAnnotationTypes] = useState([]);
  const [classificatedImagesIds, setClassificatedImagesIds] = useState([]);
  const [controlActive, setControlActive] = useState(false);
  const [currentReportName, setCurrentReportName] = useState('');
  const [eraser, setEraser] = useState(false);
  const [fileName, setFileName] = useState('');
  const [guidePosition, setGuidePosition] = useState({ x: 0, y: 0 });
  const [imagesLoading, setImagesLoading] = useState(false);
  const [initalLoading, setInitalLoading] = useState(false);
  const [initalZ, setInitalZ] = useState(0);
  const [lastExamId, setLastExamId] = useState();
  const [loadingSubmit, setLoadingSubmit] = useState('');
  const [maskImageList, setMaskImageList] = useState([]);
  const [maxDensityLimits, setMaxDensityLimits] = useState(
    MAX_DENSITY_MIN_LIMITS / 2
  );
  const [minDensityLimits, setMinDensityLimits] = useState(
    MAX_DENSITY_MIN_LIMITS / 2
  );
  const [nextExamId, setNextExamId] = useState();
  const [originalImagesIds, setOriginalImagesIds] = useState([]);
  const [patientId, setPatientId] = useState();
  const [patientLoading, setPatientLoading] = useState(false);
  const [reportImages, setReportImages] = useState([]);
  const [saveModal, setSaveModal] = useState(false);
  const [segmentationDiameter, setSegmentationDiameter] = useState(5);
  const [segmentationId, setSegmentationId] = useState();
  const [segmentationList, setSegmentationList] = useState([]);
  const [segmentationType, setSegmentationType] = useState('');
  const [selectReportOptions, setExamsSelectOptions] = useState();
  const [selectedSubtype, setSelectedSubtype] = useState();
  const [selectedTool, setSelectedTool] = useState('');
  const [selectedType, setSelectedType] = useState();
  const [showGuide, setShowGuide] = useState(false);
  const [slice, setSlice] = useState(1);
  const [slices, setSlices] = useState([]);
  const [submitWarning, setSubmitWarning] = useState();
  const [undoActionsList, setUndoActionsList] = useState([]);
  const [zoom, setZoom] = useState(1);

  useKeyDown('Control', () => setControlActive(true));

  useKeyUp('Control', () => setControlActive(false));

  useKeyDown('z', () => {
    if (controlActive) {
      const lastAction = actionsList[actionsList?.length - 1];

      if (lastAction?.type !== selectedTool) {
        setSelectedTool(lastAction?.type);
      } else if (lastAction) {
        const currentActionsList = actionsList.filter(
          (action) => action?.id !== lastAction?.id
        );
        const currentUndoActionsList = [...undoActionsList, lastAction];

        setActionsList(currentActionsList);
        setUndoActionsList(currentUndoActionsList);
      }
    }
  });

  useKeyDown('y', () => {
    if (controlActive) {
      const lastUndoAction = undoActionsList[undoActionsList?.length - 1];

      if (lastUndoAction?.type !== selectedTool) {
        setSelectedTool(lastUndoAction?.type);
      } else if (lastUndoAction) {
        const currentUndoActionsList = undoActionsList.filter(
          (action) => action?.id !== lastUndoAction?.id
        );
        const currentActionsList = [...actionsList, lastUndoAction];

        setActionsList(currentActionsList);
        setUndoActionsList(currentUndoActionsList);
      }
    }
  });

  useEffect(() => {
    if (
      breakpoint?.width &&
      breakpoint?.width < breakpoints?.ipadLandscape?.width
    ) {
      history.push('/');
    }
  }, [breakpoint?.width, history]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      setInitalLoading(true);

      const promisses = [
        Api.getMeUser(),
        Api.getReportAnnotation(id),
        Api.getAnnotationTypes(),
        Api.getReportAllImages(id),
        Api.getReportMask(id),
        Api.getReportSegmentation(id),
      ];

      const responses = await axios.all(promisses);
      const [
        meUserResponse,
        reportAnnotationResponse,
        annotationTypesResponse,
        reportAllImagesResponse,
        reportMaskResponse,
        reportSegmentationResponse,
      ] = responses;

      if (meUserResponse?.type !== 'ADM') {
        history.push('/');
      }

      if (reportSegmentationResponse?.status === 200) {
        if (reportSegmentationResponse?.data?.id) {
          setSegmentationId(reportSegmentationResponse?.data?.id);
        }

        if (reportSegmentationResponse?.data?.data?.segmentation?.length) {
          const maxLastLayer = Math.max(
            ...reportSegmentationResponse?.data?.data?.segmentation?.map(
              (s) => s.layer
            )
          );

          setInitalZ(maxLastLayer + 1);
          setSegmentationList(
            reportSegmentationResponse?.data?.data?.segmentation
          );
        }
      }

      if (reportAnnotationResponse?.status === 200) {
        if (reportAnnotationResponse?.data?.data?.annotation?.length) {
          setAnnotationList(reportAnnotationResponse?.data?.data?.annotation);
        }

        if (reportAnnotationResponse?.data?.id) {
          setAnnotationId(reportAnnotationResponse?.data?.id);
        }

        if (reportAnnotationResponse?.data?.name?.length) {
          setFileName(reportAnnotationResponse?.data?.name);
        }
      }

      if (annotationTypesResponse?.status === 200) {
        setAnnotationTypes(annotationTypesResponse?.data || []);
      }

      if (reportAllImagesResponse?.status === 200) {
        if (reportAllImagesResponse?.data?.exam?.images?.length) {
          const currentOriginalImagesIds =
            reportAllImagesResponse?.data?.exam?.images
              ?.sort((a, b) => (a.id > b.id ? 1 : -1))
              ?.map((imageId) => imageId?.value);

          setOriginalImagesIds(currentOriginalImagesIds);
          setSlices(currentOriginalImagesIds);
        }

        if (reportAllImagesResponse?.data?.images?.length) {
          setClassificatedImagesIds(
            reportAllImagesResponse?.data?.images
              ?.sort((a, b) => (a.id > b.id ? 1 : -1))
              ?.map((imageId) => imageId?.value)
          );
        }

        if (reportAllImagesResponse?.data?.exam?.patient?.id) {
          setPatientId(reportAllImagesResponse?.data?.exam?.patient?.id);
        }
      }

      if (
        reportMaskResponse?.status === 200 &&
        reportMaskResponse?.data?.length
      ) {
        setMaskImageList(
          reportMaskResponse?.data?.sort((a, b) => (a?.name > b?.name ? 1 : -1))
        );
      }

      setInitalLoading(false);
    })();
  }, [history, id, isMounted]);

  useEffect(() => {
    const currentReportImages = [];

    if (
      originalImagesIds?.length &&
      classificatedImagesIds?.length &&
      maskImageList?.length
    ) {
      for (let i = 0; i < originalImagesIds?.length; i += 1) {
        currentReportImages.push({
          classificated: { id: classificatedImagesIds[i], url: null },
          mask: { id: maskImageList[i]?.id, url: maskImageList[i]?.file },
          original: { id: originalImagesIds[i], url: null },
        });
      }
    }

    setReportImages(currentReportImages);
  }, [classificatedImagesIds, maskImageList, originalImagesIds]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      setPatientLoading(true);

      if (patientId) {
        const response = await Api.getReportsMiniByPatient(patientId);

        if (response?.data?.length) {
          let current = [];

          response?.data?.forEach((item) => {
            if (item?.id) {
              current.push({
                key: item?.id,
                value: `${item?.id}`,
                text: item?.report_name || '',
              });
            }
          });

          if (current?.length) {
            current = current?.sort((a, b) => (a?.key > b?.key ? 1 : -1));
          }

          setExamsSelectOptions(current);
        }
      }

      setPatientLoading(false);
    })();
  }, [isMounted, patientId]);

  useEffect(() => {
    if (selectReportOptions?.length && id) {
      const currentReportIndex = selectReportOptions?.findIndex(
        (exam) => exam?.value === id
      );
      const reportName = selectReportOptions[currentReportIndex]?.text;

      let currentLastExam = -1;
      let currentNextExam = -1;

      if (currentReportIndex > 0) {
        currentLastExam = selectReportOptions[currentReportIndex - 1]?.value;
      }

      if (currentReportIndex < selectReportOptions?.length - 1) {
        currentNextExam = selectReportOptions[currentReportIndex + 1].value;
      }

      if (reportName?.length) {
        setCurrentReportName(reportName);
      }

      setLastExamId(currentLastExam);
      setNextExamId(currentNextExam);
    }
  }, [id, selectReportOptions]);

  useEffect(() => {
    if (!loadingSubmit) {
      const timeout = setTimeout(() => {
        setSubmitWarning(null);
      }, 3500);

      return () => clearTimeout(timeout);
    }

    return null;
  }, [loadingSubmit]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      const startIndex = slice - 1;
      const endIndex = slice - 1 + 20;
      const currentReportImage = reportImages[startIndex];

      if (
        !currentReportImage?.original?.url ||
        !currentReportImage?.classificated?.url
      ) {
        setImagesLoading(true);

        const currentReportImages = reportImages;
        const curretReportImagesWithoutImages = reportImages
          .slice(startIndex, endIndex)
          .filter(
            (image) => !image?.original?.url || !image?.classificated?.url
          );
        const currentOriginalIds = curretReportImagesWithoutImages.map(
          (image) => image?.original?.id
        );
        const originalPromises = currentOriginalIds?.length
          ? currentOriginalIds.map((originalId) =>
              Api.getDicomJpegById(originalId)
            )
          : [];
        const currentClassificatedIds = curretReportImagesWithoutImages.map(
          (image) => image?.classificated?.id
        );
        const classificatedPromises = currentClassificatedIds?.length
          ? currentClassificatedIds.map((classificatedId) =>
              Api.getDicomJpegById(classificatedId)
            )
          : [];

        if (originalPromises?.length) {
          const originalResponses = await axios.all(originalPromises);

          originalResponses.forEach((originalResponse, index) => {
            if (originalResponse) {
              const reportImageIndex = slice - 1 + index;
              const currentImage = currentReportImages[reportImageIndex];
              const blob = new Blob([originalResponse], { type: 'image/jpeg' });

              if (currentImage) {
                currentImage.original.url = URL.createObjectURL(blob);
                currentReportImages.splice(reportImageIndex, 1, currentImage);
              }
            }
          });
        }

        if (classificatedPromises?.length) {
          const classificatedResponses = await axios.all(classificatedPromises);

          classificatedResponses.forEach((classificatedResponse, index) => {
            if (classificatedResponse) {
              const reportImageIndex = slice - 1 + index;
              const currentImage = currentReportImages[reportImageIndex];
              const blob = new Blob([classificatedResponse], {
                type: 'image/jpeg',
              });

              if (currentImage) {
                currentImage.classificated.url = URL.createObjectURL(blob);

                currentReportImages.splice(reportImageIndex, 1, currentImage);
              }
            }
          });
        }

        setReportImages(currentReportImages);
        setImagesLoading(false);
      }
    })();
  }, [isMounted, reportImages, slice]);

  const maskImagesIds = useMemo(() => {
    if (maskImageList?.length) {
      return maskImageList.map((mask) => mask.id);
    }

    return [];
  }, [maskImageList]);

  const annotationTypeOptions = useMemo(() => {
    const currentAnnotationTypes = [];

    if (annotationTypes?.length) {
      annotationTypes?.forEach((type) => {
        if (type?.id && type?.name?.length && type?.color?.length) {
          const subtypes = [];

          if (type?.subtypes?.length) {
            type?.subtypes?.forEach((subtype) => {
              if (
                subtype?.id &&
                subtype?.name?.length &&
                subtype?.color?.length
              ) {
                subtypes.push({
                  id: subtype?.id,
                  name: subtype?.name,
                  color: subtype?.color,
                });
              }
            });
          }

          currentAnnotationTypes.push({
            id: type?.id,
            name: type?.name,
            color: type?.color,
            subtypes,
          });
        }
      });
    }

    return currentAnnotationTypes;
  }, [annotationTypes]);

  const imagesAmount = useMemo(
    () => reportImages?.length || 0,
    [reportImages?.length]
  );

  const selectedSlice = useMemo(() => {
    if (slice - 1 >= 0 && slice - 1 < slices?.length) {
      return slices[slice - 1];
    }

    return null;
  }, [slice, slices]);

  const selectedClassificated = useMemo(() => {
    if (slice - 1 >= 0 && slice - 1 < classificatedImagesIds?.length) {
      return classificatedImagesIds[slice - 1];
    }

    return null;
  }, [classificatedImagesIds, slice]);

  const selectedMask = useMemo(() => {
    if (slice - 1 >= 0 && slice - 1 < maskImagesIds?.length) {
      return maskImagesIds[slice - 1];
    }

    return null;
  }, [maskImagesIds, slice]);

  const radius = useMemo(
    () => (segmentationDiameter ? (segmentationDiameter * 10) / 2 : 10),
    [segmentationDiameter]
  );

  const eraserPoints = useMemo(
    () =>
      segmentationList.filter(
        (segmentation) => segmentation?.type === 'eraser'
      ),
    [segmentationList]
  );

  const selectedImage = useMemo(() => {
    const current = reportImages[slice - 1];

    if (current) {
      return current;
    }

    return null;
  }, [reportImages, slice]);

  const handleResetZoom = () => setZoom(1);

  const handleZoomIn = () => setZoom(roundToPrecision(zoom + 0.1, 1));

  const handleZoomOut = () => {
    let currentZoom = roundToPrecision(zoom - 0.1, 1);

    if (zoom <= 0) {
      currentZoom = 0;
    }

    setZoom(currentZoom);
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setSelectedSubtype(null);
  };

  const handleSave = () => setSaveModal(true);

  const onSubmitAnnotation = () => {
    (async () => {
      if (id && annotationList?.length && fileName?.length) {
        const warningSuccess = {
          type: 'success',
          text: 'annotationSentSuccessfully',
        };
        const warningError = {
          type: 'error',
          text: 'errorWhenSendingTheAnnotation',
        };
        const annotationData = {
          data: { annotation: annotationList },
          name: fileName,
          report: id,
        };
        let warning = null;

        setLoadingSubmit(true);

        if (!annotationId) {
          const response = await Api.submitAnnotation(annotationData);

          if (response?.status === 201) {
            warning = warningSuccess;

            if (response?.data?.id) {
              setAnnotationId(response?.data?.id);
            }
          } else {
            warning = warningError;
          }
        } else {
          const response = await Api.updateAnnotation(
            annotationId,
            annotationData
          );

          if (response?.status === 200) {
            warning = warningSuccess;
          } else {
            warning = warningError;
          }
        }

        if (warning?.type === 'success') {
          setSaveModal(false);
        }

        if (warning) {
          setSubmitWarning(warning);
        }

        setLoadingSubmit(false);
      }
    })();
  };

  const onSubmitSegmentation = () => {
    (async () => {
      if (id && segmentationList?.length && fileName?.length) {
        const warningSuccess = {
          type: 'success',
          text: 'segmentationSentSuccessfully',
        };
        const warningError = {
          type: 'error',
          text: 'errorWhenSendingTheAnnotation',
        };
        const segmentationData = {
          data: { segmentation: segmentationList },
          name: fileName,
          report: id,
        };
        let warning = null;

        setLoadingSubmit(true);

        if (!segmentationId) {
          const response = await Api.submitSegmentation(segmentationData);

          if (response?.status === 201) {
            warning = warningSuccess;

            if (response?.data?.id) {
              setSegmentationId(response?.data?.id);
            }
          } else {
            warning = warningError;
          }
        } else {
          const response = await Api.updateSegmentation(
            segmentationId,
            segmentationData
          );

          if (response?.status === 200) {
            warning = warningSuccess;
          } else {
            warning = warningError;
          }
        }

        if (warning?.type === 'success') {
          setSaveModal(false);
        }

        if (warning) {
          setSubmitWarning(warning);
        }

        setLoadingSubmit(false);
      }
    })();
  };

  return (
    <EditToolStyle>
      <Header />
      <div className="edit-tool-content">
        <EditToolList
          annotationListAmount={annotationList?.length}
          imagesAmount={imagesAmount}
          maxDensityLimits={maxDensityLimits}
          minDensityLimits={minDensityLimits}
          segmentationDiameter={segmentationDiameter}
          slice={slice}
          zoom={zoom}
          eraser={eraser}
          lastExamId={lastExamId}
          nextExamId={nextExamId}
          reportId={id}
          segmentationLung={segmentationType}
          selectedSubtype={selectedSubtype}
          selectedTool={selectedTool}
          selectedType={selectedType}
          annotationTypeOptions={annotationTypeOptions}
          reportOptions={selectReportOptions}
          updateEraser={setEraser}
          updateFileName={setFileName}
          updateMaxDensityLimits={setMaxDensityLimits}
          updateMinDensityLimits={setMinDensityLimits}
          updateSegmentationDiameter={setSegmentationDiameter}
          updateSegmentationLung={setSegmentationType}
          updateSelectedSubtype={setSelectedSubtype}
          updateSelectedTool={setSelectedTool}
          updateSlice={setSlice}
          handleResetZoom={handleResetZoom}
          handleSave={handleSave}
          handleSelectType={handleSelectType}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
        />
        <div className="exam-images-container">
          <div ref={orignalContainerRef} className="orignal-container">
            <div className="images-wrapper">
              {selectedImage?.original?.url?.length ? (
                <img
                  className="image-item active"
                  src={selectedImage?.original?.url}
                  alt={`Original ${selectedImage?.original?.id}`}
                />
              ) : null}
            </div>
            {selectedTool === EDIT_TOOL_TYPES?.segmentation?.key ? (
              <div className="images-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                  <defs>
                    <mask id="eraser-mask">
                      <rect width="100%" height="100%" fill="white" />
                      {eraserPoints?.length
                        ? eraserPoints?.map((point) => {
                            if (point) {
                              const r = point?.impactedSlices.find(
                                (iS) => iS?.id === selectedSlice
                              )?.radius;

                              return (
                                <circle
                                  key={point?.id}
                                  fill="black"
                                  className={`type-${point?.type}`}
                                  cx={point?.x}
                                  cy={point?.y}
                                  r={r}
                                />
                              );
                            }

                            return null;
                          })
                        : null}
                    </mask>
                  </defs>
                  {selectedImage?.mask?.url?.length ? (
                    <image
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      xlinkHref={selectedImage?.mask?.url}
                      mask="url(#eraser-mask)"
                      height="100%"
                      width="100%"
                    />
                  ) : null}
                </svg>
              </div>
            ) : null}
            {selectedTool === EDIT_TOOL_TYPES?.annotation?.key ? (
              <AnnotationCanvas
                slice={selectedSlice}
                eraser={eraser}
                tool={selectedTool}
                orignalRef={orignalContainerRef}
                subtype={selectedSubtype}
                type={selectedType}
                actionsList={actionsList}
                annotations={annotationList}
                undoActionsList={undoActionsList}
                updateActionsList={setActionsList}
                updateAnnotationList={setAnnotationList}
                updateGuidePosition={setGuidePosition}
                updateShowGuide={setShowGuide}
              />
            ) : null}
            {selectedTool === EDIT_TOOL_TYPES?.segmentation?.key ? (
              <SegmentationCanvas
                original={selectedSlice}
                classificated={selectedClassificated}
                mask={selectedMask}
                eraser={eraser}
                radius={radius}
                imageIndex={slice}
                tool={selectedTool}
                type={segmentationType}
                orignalRef={orignalContainerRef}
                segmentations={segmentationList}
                originals={slices}
                updateGuidePosition={setGuidePosition}
                updateSegmentationList={setSegmentationList}
                updateShowGuide={setShowGuide}
                initalZ={initalZ}
                classificateds={classificatedImagesIds}
                masks={maskImagesIds}
                actionsList={actionsList}
                updateActionsList={setActionsList}
                undoActionsList={undoActionsList}
              />
            ) : null}
          </div>
          <div className="classificated-container">
            <div className="images-wrapper">
              {selectedImage?.classificated?.url?.length ? (
                <img
                  className="image-item active"
                  src={selectedImage?.classificated?.url}
                  alt={`Classificated ${selectedImage?.classificated?.id}`}
                />
              ) : null}
            </div>
            {(selectedTool === EDIT_TOOL_TYPES?.annotation?.key ||
              selectedTool === EDIT_TOOL_TYPES?.segmentation?.key) &&
            showGuide ? (
              <>
                <div
                  className="horizontal-guide"
                  style={{ top: `${guidePosition.y}px` }}
                />
                <div
                  className="vertical-guide"
                  style={{ left: `${guidePosition.x}px` }}
                />
                {selectedTool === EDIT_TOOL_TYPES?.annotation?.key ? (
                  <div
                    className="center-guide"
                    style={{
                      left: `${guidePosition.x - 10}px`,
                      top: `${guidePosition.y - 10}px`,
                    }}
                  />
                ) : null}
                {selectedTool === EDIT_TOOL_TYPES?.segmentation?.key ? (
                  <div
                    className="center-guide"
                    style={{
                      height: `${radius * 2}px`,
                      left: `${guidePosition.x - radius}px`,
                      top: `${guidePosition.y - radius}px`,
                      width: `${radius * 2}px`,
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
      {saveModal ? (
        <Modal theme="light" handleClose={() => setSaveModal(false)}>
          <>
            <div className="save-modal-container">
              <h3 className="save-modal-title">
                {selectedTool === EDIT_TOOL_TYPES?.annotation?.key
                  ? `${translate('saveYourAnnotationAs', language)}:`
                  : ''}
                {selectedTool === EDIT_TOOL_TYPES?.segmentation?.key
                  ? `${translate('saveYourSegmentationAs', language)}:`
                  : ''}
              </h3>
              <p className="save-modal-subtitle">{`${translate(
                'nameRepportExamToSaveAs',
                language
              )}:`}</p>
              <div className="annotation-file-name">
                <span className="annotation-prefix">
                  {`${currentReportName || ''}/`}
                </span>
                <span className="file-name-field">
                  <InputField
                    inputName="file-name"
                    value={fileName}
                    onChange={setFileName}
                    noMarginBottom
                  />
                </span>
              </div>
              <div className="actions-wrapper">
                <div className="action-item">
                  <ActionButton
                    theme="light-disabled"
                    text="CANCEL"
                    onClick={() => setSaveModal(false)}
                  />
                </div>
                <div className="action-item">
                  {selectedTool === EDIT_TOOL_TYPES?.annotation?.key ? (
                    <ActionButton
                      theme="default"
                      text="SAVE"
                      onClick={onSubmitAnnotation}
                      disabled={!fileName?.length}
                    />
                  ) : null}
                  {selectedTool === EDIT_TOOL_TYPES?.segmentation?.key ? (
                    <ActionButton
                      theme="default"
                      text="SAVE"
                      onClick={onSubmitSegmentation}
                      disabled={!fileName?.length}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </>
        </Modal>
      ) : null}
      {submitWarning?.type?.length && submitWarning?.text?.length ? (
        <div className="snack-bar-wrapper">
          <SnackBar
            type={submitWarning?.type}
            text={translate(submitWarning?.text, language)}
          />
        </div>
      ) : null}
      {initalLoading || patientLoading || imagesLoading || loadingSubmit ? (
        <Loading />
      ) : null}
    </EditToolStyle>
  );
};

export default EditTool;
